import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Navbar from "../components/Navbar";
import SideBar from "../components/SideBar";
import SideBarMobile from "../components/SideBarMobile";
import { getAdminNavbarItems, getAdminSidebarItems, resolveAdminActiveNavbar } from "./navigationItems";
import { useShowAdminProfileQuery } from "../services/organization/auth";
import { setCurrentUser } from "../app/store/reducers/authSlice";
import { useTranslation } from "react-i18next";
import { setDocumentLang } from "../utils/helpers";
import i18n from "../i18n";
import { useAdminGetSubscriptionQuery } from "../services/organization/subscription";
import { useGetSubscriptionPlansQuery } from "../services/public/subscriptionPlans";
import ManageSubscriptionModal from "../components/Subscription/modals/ManageSubscriptionModal";
import SuccessModal from "../components/Modals/SuccessModal";
import {
  SUBSCRIPTION_STATUS_EXPIRED,
  SUBSCRIPTION_STATUS_CANCELLED,
} from "../constants";

const DISMISSED_MODAL_KEY = "expired_modal_dismissed_session";

const AdminLayout = ({ activeNavbar: activeNavbarProp, activeSidebar }) => {
  const { data: myProfile, error } = useShowAdminProfileQuery();
  const location = useLocation();
  const activeNavbar =
    resolveAdminActiveNavbar(location.pathname) || activeNavbarProp || "";

  const { t, i18n } = useTranslation([
    "common",
    "learningPaths",
    "leaderboard",
    "communityFeed",
    "subscription",
  ]);

  let navigate = useNavigate();
  const dispatch = useDispatch();

  // Subscription data for global expired popup 
  const { data: subscriptionResp, refetch: refetchSubscription } =
    useAdminGetSubscriptionQuery({}, { refetchOnMountOrArgChange: true });
  const { data: plansResp } = useGetSubscriptionPlansQuery({locale: i18n.language});

  const subscription = subscriptionResp?.data?.subscription || null;

  const rawPlans = plansResp?.data || [];
  const currentPlanId = subscription?.plan?.id;
  const upgradePlans = useMemo(
    () =>
      rawPlans
        .filter((p) => p?.is_active)
        .filter((p) => (currentPlanId ? p?.id !== currentPlanId : true)),
    [rawPlans, currentPlanId]
  );

  // Show global modal when subscription is expired or cancelled AND the period has actually ended
  const isExpiredOrCancelled = subscription?.status === SUBSCRIPTION_STATUS_EXPIRED || 
                               (subscription?.status === SUBSCRIPTION_STATUS_CANCELLED && 
                                subscription?.expires_at && 
                                moment().isAfter(moment(subscription.expires_at)));
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (isExpiredOrCancelled) {
      setShowExpiredModal(true);
    }
  }, [isExpiredOrCancelled]);

  const handleExpiredModalClose = () => {
    setShowExpiredModal(false);
  };

  const handleRenewalSuccess = () => {
    setShowExpiredModal(false);
    setShowSuccessModal(true);
    refetchSubscription();
    // Clear dismiss flag so next login the modal won't auto-show again
    sessionStorage.removeItem(DISMISSED_MODAL_KEY);
  };

  useEffect(() => {
    if (myProfile?.status == "success") {
      dispatch(setCurrentUser(myProfile?.data));
      i18n.changeLanguage(myProfile?.data?.admin?.language);
      setDocumentLang(myProfile?.data?.admin?.language);
    }

    if (error?.status == 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("is_admin");
      dispatch(setCurrentUser(null));
      navigate("/login/admin");
    }
  }, [myProfile, error]);

  return (
    <div className="admin-layout">
      <Navbar
        items={getAdminNavbarItems(activeNavbar, t)}
        oraganizationLogo={myProfile?.data ? myProfile.data.image_url : ""}
        isAdmin
      ></Navbar>
      <div className="container-fluid">
        <div className="row">
          {activeSidebar && (
            <SideBar items={getAdminSidebarItems(activeSidebar, t)} />
          )}

          <SideBarMobile
            items={getAdminNavbarItems(activeNavbar, t)}
            additionalItems={getAdminSidebarItems(activeSidebar, t)}
            isAdmin
          />
          <div className="col content-page">
            {/* This element will render child of AdminLayout */}
            <Outlet />
          </div>
        </div>
      </div>

      {/* Global Expired/Cancelled Subscription Popup */}
      <ManageSubscriptionModal
        isOpened={showExpiredModal}
        onClose={handleExpiredModalClose}
        subscription={subscription}
        upgradePlans={upgradePlans}
        onSuccess={handleRenewalSuccess}
      />

      <SuccessModal
        isOpened={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onButtonClick={() => setShowSuccessModal(false)}
        title={t("subscription:retryPayment.successTitle", "Payment Successful!")}
        description={t(
          "subscription:retryPayment.successDesc",
          "Your payment has been processed successfully. Your subscription is now active."
        )}
        buttonLabel={t("common:done", "Done")}
      />
    </div>
  );
};

export default AdminLayout;
